/**
 * LegalSignaturesPage - Admin Panel
 * Il Diario di Bordo - SaaS
 * 
 * Dashboard per visualizzare lo stato delle firme per ogni documento:
 * - Lista organization/utenti con stato firma
 * - Statistiche completamento
 * - Invio reminder manuali
 * - Export CSV
 * 
 * @version 1.0.0
 * @created Gennaio 2025
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { adminApi } from '../services/api'
import {
  ChevronLeft,
  Search,
  Filter,
  Download,
  Send,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Users,
  Calendar,
  Loader2,
  Mail,
  Phone,
  ExternalLink,
  ChevronRight
} from 'lucide-react'

export default function LegalSignaturesPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  
  const [document, setDocument] = useState(null)
  const [signatures, setSignatures] = useState([])
  const [stats, setStats] = useState({ total: 0, signed: 0, pending: 0, percentage: 0 })
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  
  // Filtri
  const [statusFilter, setStatusFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)

  const loadSignatures = useCallback(async () => {
    try {
      setLoading(true)
      const params = { page, limit: 20 }
      if (statusFilter) params.status = statusFilter
      
      const response = await adminApi.getLegalDocumentSignatures(id, params)
      
      let sigs = response.data.data.signatures || []
      
      // Filtro client-side per search
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        sigs = sigs.filter(s => 
          s.organizationName?.toLowerCase().includes(query) ||
          s.responsabile?.nome?.toLowerCase().includes(query) ||
          s.responsabile?.cognome?.toLowerCase().includes(query) ||
          s.responsabile?.email?.toLowerCase().includes(query) ||
          s.nome?.toLowerCase().includes(query) ||
          s.cognome?.toLowerCase().includes(query) ||
          s.email?.toLowerCase().includes(query)
        )
      }
      
      setSignatures(sigs)
      setStats(response.data.data.stats || { total: 0, signed: 0, pending: 0, percentage: 0 })
      setDocument(response.data.data.document || null)
      setPagination(response.data.data.pagination || { page: 1, pages: 1, total: 0 })
      
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [id, page, statusFilter, searchQuery])

  useEffect(() => {
    loadSignatures()
  }, [loadSignatures])

  const handleSendReminder = async (organizationId = null) => {
    const message = organizationId 
      ? 'Inviare reminder a questa organization?' 
      : 'Inviare reminder a TUTTE le organization che non hanno ancora firmato?'
    
    if (!confirm(message)) return
    
    try {
      setSending(true)
      setError(null)
      
      // Per ora inviamo reminder globale
      const response = await adminApi.sendLegalDocumentReminder(id)
      
      setSuccess(`Reminder inviati: ${response.data.data.notificationsSent} email`)
      setTimeout(() => setSuccess(null), 5000)
      
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nell\'invio reminder')
    } finally {
      setSending(false)
    }
  }

  const handleRequestResignature = async () => {
    if (!confirm('Richiedere nuova firma a TUTTE le organization? Le firme esistenti verranno invalidate.')) {
      return
    }
    
    const deadlineDays = prompt('Quanti giorni di deadline? (es: 30)', '30')
    if (!deadlineDays) return
    
    try {
      setSending(true)
      setError(null)
      
      const response = await adminApi.requestLegalDocumentResignature(id, {
        deadlineDays: parseInt(deadlineDays),
        sendNotification: true
      })
      
      setSuccess(`Ri-firma richiesta. Firme invalidate: ${response.data.data.invalidatedSignatures}. Notifiche inviate: ${response.data.data.notificationsSent}`)
      setTimeout(() => setSuccess(null), 8000)
      
      loadSignatures()
      
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella richiesta ri-firma')
    } finally {
      setSending(false)
    }
  }

  const handleExportCSV = () => {
    // Genera CSV
    const headers = ['Organization', 'Responsabile', 'Email', 'Stato', 'Data Firma']
    const rows = signatures.map(s => [
      s.organizationName || `${s.nome} ${s.cognome}`,
      s.responsabile ? `${s.responsabile.nome} ${s.responsabile.cognome}` : '-',
      s.responsabile?.email || s.email || '-',
      s.status === 'signed' ? 'Firmato' : s.status === 'expired' ? 'Scaduto' : 'In attesa',
      s.signedAt ? new Date(s.signedAt).toLocaleString('it-IT') : '-'
    ])
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n')
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = window.document.createElement('a')
    link.href = url
    link.download = `firme-${document?.title || 'documento'}-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const getStatusBadge = (status) => {
    const badges = {
      signed: { 
        color: 'bg-green-100 text-green-700', 
        icon: <CheckCircle className="h-4 w-4" />, 
        label: 'Firmato' 
      },
      pending: { 
        color: 'bg-yellow-100 text-yellow-700', 
        icon: <Clock className="h-4 w-4" />, 
        label: 'In attesa' 
      },
      expired: { 
        color: 'bg-red-100 text-red-700', 
        icon: <XCircle className="h-4 w-4" />, 
        label: 'Scaduto' 
      }
    }
    const badge = badges[status] || badges.pending
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.icon} {badge.label}
      </span>
    )
  }

  if (loading && signatures.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/legal-documents')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            Dashboard Firme
          </h1>
          {document && (
            <p className="text-gray-500">
              {document.title} v{document.version}
              {document.deadline && (
                <span className={`ml-2 ${document.isDeadlineExpired ? 'text-red-600' : 'text-gray-500'}`}>
                  • Deadline: {new Date(document.deadline).toLocaleDateString('it-IT')}
                  {document.isDeadlineExpired && ' (Scaduta)'}
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-gray-500">Totale</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.signed}</p>
              <p className="text-sm text-gray-500">Firmati</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-gray-500">In attesa</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${
              stats.percentage === 100 ? 'bg-green-100' :
              stats.percentage >= 80 ? 'bg-blue-100' :
              stats.percentage >= 50 ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              <div className={`text-lg font-bold ${
                stats.percentage === 100 ? 'text-green-600' :
                stats.percentage >= 80 ? 'text-blue-600' :
                stats.percentage >= 50 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {stats.percentage}%
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Completamento</p>
              <div className="w-24 h-2 bg-gray-200 rounded-full mt-1">
                <div 
                  className={`h-full rounded-full ${
                    stats.percentage === 100 ? 'bg-green-500' :
                    stats.percentage >= 80 ? 'bg-blue-500' :
                    stats.percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${stats.percentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions & Filters */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          <button
            onClick={() => handleSendReminder()}
            disabled={sending || stats.pending === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Invia Reminder a Tutti
          </button>
          
          <button
            onClick={handleRequestResignature}
            disabled={sending}
            className="px-4 py-2 border border-orange-500 text-orange-600 rounded-lg hover:bg-orange-50 flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Richiedi Ri-firma
          </button>
          
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Esporta CSV
          </button>
        </div>
        
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-lg w-64"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Tutti gli stati</option>
            <option value="signed">Firmati</option>
            <option value="pending">In attesa</option>
            <option value="expired">Scaduti</option>
          </select>
          
          <button
            onClick={loadSignatures}
            className="p-2 border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Signatures Table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Organization / Utente
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Responsabile
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Email
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Stato
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                Data Firma
              </th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-500">
                Azioni
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {signatures.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  Nessun risultato trovato
                </td>
              </tr>
            ) : (
              signatures.map((sig, idx) => (
                <tr key={sig.organizationId || sig.userId || idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">
                      {sig.organizationName || `${sig.nome} ${sig.cognome}`}
                    </p>
                    {sig.tenantName && (
                      <p className="text-sm text-gray-500">{sig.tenantName}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {sig.responsabile ? (
                      <span>{sig.responsabile.nome} {sig.responsabile.cognome}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <a 
                      href={`mailto:${sig.responsabile?.email || sig.email}`}
                      className="text-primary-600 hover:underline text-sm"
                    >
                      {sig.responsabile?.email || sig.email}
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(sig.status)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {sig.signedAt 
                      ? new Date(sig.signedAt).toLocaleString('it-IT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : '-'
                    }
                  </td>
                  <td className="px-4 py-3 text-right">
                    {sig.status !== 'signed' && (
                      <button
                        onClick={() => handleSendReminder(sig.organizationId || sig.userId)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                        title="Invia reminder"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Pagina {page} di {pagination.pages} ({pagination.total} risultati)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={page === pagination.pages}
              className="px-3 py-1 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
